// no itx for this file
import type Device from "./Device";
import KnxInstanceData, { ValueLevels } from "./KnxInstanceData";
import KnxRule, { RuleIds } from "./KnxRule";

import Logger from "../Logger";
import type Channel from "./Channel";
import BaseObject from "./BaseObject";
import type ButtonChannel from "./ButtonChannel";
import type Button from "./Button";

import type KnxProductData from "../KnxProductdata";
import type Signal from "./Signal";
import { getRequestChannelforMapSignalToTagResult } from "../KnxProductDataTools/groupGeneralSignalsIntoChannelsForActor";
import { ChannelType, channelTypeInfo } from "./ChannelType";
import { mapSignal, MapSignalParameters } from "./mapSignal";
import type { MapChannelTypeConfig } from "../components/PanelLayout/MapChannelTypeConfig";
import { PanelConfigs } from "../components/PanelLayout/PanelConfig";
import type { MapChannelTextConfig } from "../components/PanelLayout/MapChannelTypeConfig";
const logger = new Logger();

export interface SceneDefinitions {
  //
  // Scene-Property detection:
  //
  // if globalEnable has a definition of a MatchGroup,
  // the result of this matchGroup is used as ChannelName .......
  //

  enableForChannel?: string | RegExp;
  enable?: string | RegExp;
  number?: string | RegExp;
  value1?: string | RegExp;
  value2?: string | RegExp;
}

export interface Definitions {
  scenes?: SceneDefinitions;
}

import { RockerConfig } from "./ButtonRockerConfig";
export { RockerConfig } from "./ButtonRockerConfig";

export type ResultObjectButtonSide = "l" | "r" | "both" | "single";
export type ButtonNumber = number | "slap" | "temperature" | string;

export interface MapSignalToTagResult {
  tag?: string;
  channelName?: string;
  channelType?: string; // "fromRequest"  |  "switch" | "dimmer" | "jalousie" | etc.;

  rockerType?: RockerConfig;

  signalType?: string;

  buttonSide?: ResultObjectButtonSide;

  buttonNumber?: ButtonNumber;
  longPress?: boolean;
  extraLongPress?: boolean;

  statusNumber?: number | string;

  channelNumber?: number | string;
  specialNumber?: number | string;

  logicNumber?: number | string;
  logicInput?: boolean;
  logicOutput?: boolean;

  ignore?: boolean; // irgnore this signal
}

export default class KnxRules extends BaseObject {
  public get className(): string {
    return "KnxRules";
  }

  constructor(parent: KnxProductData) {
    super({}, parent);
  }

  _rules: Map<string, KnxRule> | undefined;
  avoidOnChangeId?: string | undefined;

  toJSON(): any | undefined {
    if (this._rules && this._rules.size > 0) {
      const rr: any = [];

      this._rules.forEach((rule) => {
        rr.push(rule.toJSON());
      });

      // console.log("KnxRules.toJSON: ", rr);

      return { rules: rr };
    }

    return undefined;
  }

  fromJSON(data: any | undefined): void {
    if (data) {
      this._rules = new Map<string, KnxRule>();

      (data.rules as Array<any>)?.forEach((itemData: any) => {
        const rule = new KnxRule();
        rule.fromJSON(itemData);
        this.set(rule);
      });
    }
  }

  init() {
    if (!this._rules) {
      this._rules = new Map();
    }
  }

  get empty(): boolean {
    return this._rules == undefined || this._rules.size == 0;
  }

  asArray(): Array<KnxRule> {
    const a = Array<KnxRule>();
    if (this._rules) {
      this._rules.forEach((element) => {
        a.push(element as KnxRule);
      });
    }
    return a;
  }

  get(rule_id: RuleIds, param_id?: string, param_instanceId?: string): KnxRule | undefined {
    const fullId = KnxRule.calcId(rule_id, param_id, param_instanceId);
    return this._rules?.get(fullId);
  }

  set(rule: KnxRule) {
    if (!this._rules) {
      this._rules = new Map();
    }
    this._rules?.set(rule.calcId(), rule);
  }

  getOrCreate(rule_id: RuleIds, param_id?: string, param_instanceId?: string): KnxRule {
    const fullId = KnxRule.calcId(rule_id, param_id, param_instanceId);

    this.init();

    let rule = this._rules?.get(fullId);
    if (!rule) {
      rule = new KnxRule(rule_id, param_id, param_instanceId);
      rule._code = "";

      // if (false) {
      //   rule._code = `// ${rule_id}`;
      //   if (param_id) {
      //     rule._code += ` id=${param_id}`;
      //   }
      //   if (param_instanceId) {
      //     rule._code += ` instanceId=${param_instanceId}`;
      //   }
      // }

      this._rules?.set(fullId, rule);
    }

    return rule;
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////

  executeOnChange(
    device: Device,
    knxInstancedata: KnxInstanceData,
    id: string,
    instanceId: string,
    moduleId: string,
    value: any,
    lastValue: any,
  ) {
    if (device.isDummyDeviceForTemplate) {
      return;
    }

    if (this.avoidOnChangeId == `${id}-${instanceId}`) {
      return;
    }

    if (this._rules) {
      const rule = this._rules.get(KnxRule.calcId(RuleIds.onChange, id, instanceId));
      if (rule && rule.code) {
        rule.execute(this._rules, device, knxInstancedata, id, instanceId, moduleId, {
          newValue: value,
          lastValue: lastValue,
        });
      }
    }
  }

  //
  // returns true, if at least one param changed
  //

  pullAllValues(device: Device, info: any): boolean {
    if (device.isDummyDeviceForTemplate) {
      return false;
    }

    let alLeastOneParamChanged = false;

    logger.log("pullAllValues");

    const rule = this._rules?.get(RuleIds.onPullAll);
    if (rule) {
      rule.execute(this._rules, device, device.knxInstanceData, "", "", "", {
        info: info,
        device: device,

        copyProperty: (params: any) => {
          const sourceId = params.sourceId;
          let targetIds = params.targetId;

          if (!Array.isArray(targetIds)) {
            targetIds = [targetIds];
          }

          for (const instanceId of device.knxInstanceData.allModuleIds()) {
            const sourceValue = device.knxInstanceData.getValue(sourceId, instanceId, false);
            if (sourceValue != undefined) {
              for (const targetId of targetIds) {
                const lastTargetValue = device.knxInstanceData.getValue(targetId, instanceId, false);

                //
                // nur wenn es die property wirklich gibt, wird sie zugewiesen.
                //
                if (lastTargetValue != undefined && lastTargetValue != sourceValue) {
                  device.knxInstanceData.setValue(ValueLevels.device, sourceValue, targetId, instanceId, "");
                }
              }
            }
          }
        },
      });
    }

    this._rules?.forEach((rule) => {
      if (rule.rule_id == RuleIds.onPull && rule.param_id && rule.code) {
        //
        // legt fest ob die Wippen einzeln als Taster oder als Wipper benutzt werden
        //

        const res = rule.execute(this._rules, device, device.knxInstanceData, "", "", "", {
          info: info,
          device: device,
        });

        if (res == undefined) {
          logger.log(
            `ERROR: Script "onPull" without result: missing return statement !? \n id=${rule.param_id} \n instanceId=${rule.param_instanceId} \n ${rule.code} \n ================================\n `,
          );
        } else {
          let value = "";
          let level = ValueLevels.calculatedByRule;

          if (typeof res === "object") {
            value = res.value;
            level = res.level;
          } else {
            value = res;
          }

          logger.log(`onPull ${rule.param_id}: value=${value} level=${level}`);

          if (value != undefined) {
            this.avoidOnChangeId = `${rule.param_id}-${rule.param_instanceId}`;
            const newValue = `${value}`;
            const lastValue = device.knxInstanceData.getValue(rule.param_id, rule.param_instanceId || "");
            if (lastValue != newValue) {
              alLeastOneParamChanged = true;
              device.knxInstanceData.setValue(
                level,
                newValue,
                rule.param_id,
                rule.param_instanceId || "",
                "",
                true,
                true,
              );
            }

            const valueExt = device.knxInstanceData.getExtendedData(rule.param_id, rule.param_instanceId || "") || {};
            valueExt.mbe = { isSet: true };

            device.knxInstanceData.setExtendedData(valueExt, rule.param_id, rule.param_instanceId || "");

            logger.log(`set pullValues ${rule.param_id} ${rule.param_instanceId} to ${value}`);
            this.avoidOnChangeId = undefined;
          }
        }
      }
    });

    return alLeastOneParamChanged;
  }

  setRockerConfig(button: Button, buttonChannel: ButtonChannel, deleteChannel?: boolean): any {
    if (button.isDummyDeviceForTemplate) {
      return undefined;
    }

    if (
      buttonChannel.isLogicChannel ||
      buttonChannel.isStatusChannel ||
      buttonChannel.isActorChannel ||
      channelTypeInfo(buttonChannel.channelType).isHeating ||
      buttonChannel.channelType == ChannelType.Scene ||
      buttonChannel.tag.startsWith("TP") // Patch-Taste
    ) {
      return undefined;
    }

    const rule = this._rules?.get(RuleIds.setRockerConfig);

    buttonChannel.assetIsRequestChannel();

    //
    // MWE 7.8.2025
    // wir müssen auch ohne knxChannel setRockerConfig ausführen,
    // weil der knxChannel gegebenfalls dadurch erst entsteht
    //

    // if (buttonChannel.knxChannel == undefined) {
    //   return undefined
    // }

    if (rule && rule.code) {
      if (rule?.monitor) {
        rule.outputSeparator();
        rule.output(` (tag=${buttonChannel.tag.substring(0, 3)} type=${buttonChannel.rockerConfig})`);
      }

      const tag = buttonChannel.tag.substring(0, 3);
      const res = rule.execute(this._rules, button as Device, button.knxInstanceData, "", "", "", {
        tag: buttonChannel.tag.substring(0, 3),
        type: deleteChannel ? RockerConfig.disabled : buttonChannel.rockerConfig,
        channel: buttonChannel,
        channelType: buttonChannel.channelType,
        setRocker: (params: any) => {
          const buttonNumber = buttonChannel.buttonNumber;
          if (!isNaN(buttonNumber)) {
            (buttonChannel.parent as Button)?.setRockerConfigFromScript(rule, buttonChannel.buttonNumber, params);
          }
        },
      });

      return res;
    }

    return undefined;
  }

  setChannelType(button: Button, channel: Channel): any {
    if (button.isDummyDeviceForTemplate) {
      return undefined;
    }

    const rule = this._rules?.get(RuleIds.setChannelType);
    let type = channel.channelType;
    let rockerConfig = "";
    if (channel.className == "ButtonChannel") {
      rockerConfig = (channel as ButtonChannel).rockerConfig;
    }

    if (rule?.monitor) {
      rule.outputSeparator();
      rule.output(`( tag=${channel.tag} type=${type} rockerType=${rockerConfig})`);
    }

    if (rule && rule.code) {
      return rule.execute(this._rules, button as Device, button.knxInstanceData, "", "", "", {
        tag: channel.tag,
        type: type,
        channel: channel,
        rockerType: rockerConfig,
      });
    }

    return undefined;
  }

  panelConfig(device: Device): PanelConfigs | undefined {
    let res: PanelConfigs | undefined = undefined;

    const rule = this._rules?.get(RuleIds.panelConfig);
    if (rule && rule.code) {
      res = rule.execute(this._rules, device, device.knxInstanceData, "", "", "", {});
      if (rule?.monitor) {
        rule.outputSeparator();
        rule.output(JSON.stringify(res, null, 2));
      }
    }

    return res;
  }

  mapChannelText(device: Device, channel: Channel, text: string): MapChannelTextConfig | undefined {
    let res: MapChannelTextConfig | undefined = undefined;

    const rule = this._rules?.get(RuleIds.mapChannelText);
    if (rule && rule.code) {
      res = rule.execute(this._rules, device, device.knxInstanceData, "", "", "", {
        tag: channel.tag,
        channel: channel,
        text: text,
      });

      if (rule?.monitor) {
        rule.outputSeparator();
        rule.output(JSON.stringify(res, null, 2));
      }
    }

    return res;
  }

  mapChannelType(device: Device): MapChannelTypeConfig | undefined {
    let res: MapChannelTypeConfig | undefined = undefined;

    const rule = this._rules?.get(RuleIds.mapChannelType);
    if (rule && rule.code) {
      res = rule.execute(this._rules, device, device.knxInstanceData, "", "", "", {});
      if (rule?.monitor) {
        rule.outputSeparator();
        rule.output(JSON.stringify(res, null, 2));
      }
    }

    return res;
  }

  mapChannelToTag(button: Device, knxChannel: Channel): string | undefined {
    if (button.isDummyDeviceForTemplate) {
      return undefined;
    }

    knxChannel.assetIsKnxChannel();

    const rule = this._rules?.get(RuleIds.mapChannelToTag);

    if (rule?.monitor) {
      rule.outputSeparator();
      rule.output(`( channelName=${knxChannel.name} channel=${knxChannel} )`);
    }

    if (rule && rule.code) {
      const res = rule.execute(this._rules, button, button.knxInstanceData, "", "", "", {
        channel: knxChannel,
        button: button,
        channelName: knxChannel.name,
      });

      if (rule.monitor) {
        rule.output(`\n  res= ${res}\n `);
      }

      return res;
    }

    return undefined;
  }

  definitions(button: Device): Definitions | undefined {
    if (button.isDummyDeviceForTemplate) {
      return undefined;
    }

    const rule = this._rules?.get(RuleIds.definitions);
    if (rule && rule.code) {
      const res = rule.execute(this._rules, button, button.knxInstanceData, "", "", "", {});
      if (rule.monitor) {
        rule.output(`definitions`, res);
      }

      return res as Definitions;
    }
  }

  mapSignalToTag(button: Device, signal: Signal): string | MapSignalToTagResult | undefined {
    if (button.isDummyDeviceForTemplate) {
      return undefined;
    }
    let rule = this._rules?.get(RuleIds.mapSignalToTag);

    if (!rule || !rule.code || rule.code.trim() == "") {
      //
      // get default rule for manufacturer or generic
      //

      rule = button.template?.knxProductData.getDefaultRule(button, RuleIds.mapSignalToTag);
      if (rule) {
        // rule.monitor = true;
      }
    }

    if (rule && rule.code) {
      const lastRuleMonitor = rule.monitor;

      if (state.scriptOutputFilterSignalNumber.value) {
        rule.monitor = state.scriptOutputFilterSignalNumber.value == signal.number;
      }

      if (rule.monitor) {
        rule.outputSeparator();
        rule.output(`( signalNumber=${signal.number})`);
      }

      const res = rule.execute(this._rules, button, button.knxInstanceData, "", "", "", {
        signal: signal,
        signalNumber: parseInt(signal.number),
        signalText: signal.knxTextENForDetection,
        signalTextRaw: signal.knxTextENForDetectionRaw,
        signalFunction: signal.knxFunctionTextEN,

        number: parseInt(signal.number),
        button: button,

        mapSignal: (params: MapSignalParameters, matchText?: string) => {
          return mapSignal(rule, signal, params, matchText);
        },

        getRequestChannel: (resultObject: MapSignalToTagResult) => {
          return getRequestChannelforMapSignalToTagResult(button as Button, resultObject);
        },
      });

      if (rule.monitor) {
        rule.output(`result`, res);
      }

      rule.monitor = lastRuleMonitor;

      return res;
    }

    return undefined;
  }
}
